import * as API from '@/services/api';

export async function userRequest() {
  const response = await API.get('/api/user');
  return response.data;
}

export async function accountRequest() {
  const response = await API.get('/api/account');
  return response.data;
}

export async function createClientRequest(client, productConfigurationId) {
  const response = await API.post('/api/u', {
    ...client,
    productConfigurationId,
  });
  return response.data;
}

export async function deleteClientRequest(clientToken, productType) {
  const response = await API.post(`/api/admin/bq_user/${clientToken}/deactivate`, {
    productType,
  });
  return response.data;
}

export async function changePassword(userId, currentPassword, password) {
  const response = await API.patch(`/api/user/${userId}/password`, {
    currentPassword,
    password,
  });
  return response;
}

export async function updateClient(client) {
  const response = await API.patch('/api/admin/bq_user', client);
  return response;
}

export async function updateClientSettings(clientSettings) {
  const response = await API.post('/api/admin/user/settings', clientSettings);
  return response;
}

export async function recoverPassword(passwordResetRequest) {
  return API.post('/api/user/password/reset', passwordResetRequest);
}

export async function resetPassword(passwordResetRequest) {
  return API.post('/api/user/password', passwordResetRequest);
}

export async function verifyPasswordResetToken(verifyToken) {
  return API.get(`/api/user/password/reset/verify?token=${verifyToken}`);
}

export async function addUserToAnonymizationAllowList(productConfigurationId, bqUserToken) {
  return API.post(`/api/productconfig/${productConfigurationId}/user/anonymization_allow_list`, {
    userToken: bqUserToken,
  });
}

export async function removeUserFromAnonymizationAllowList(productConfigurationId, bqUserToken) {
  return API.del(`/api/productconfig/${productConfigurationId}/anonymization_initiator/participant/${bqUserToken}`);
}

export async function verifyEmailToken(emailVerificationToken) {
  return API.get(`/api/email/verification/${emailVerificationToken}`);
}

export async function submitEmail(email) {
  return API.post('/api/presignup/bqaself', email);
}

export async function register(registrationData) {
  return API.post('/api/signup', registrationData);
}

export async function submitEmailBQPSelfAssessment(email) {
  return API.post('/api/presignup/selfassessment', email);
}

export async function registerBQPSelfAssessment(registrationData) {
  return API.post('/api/signup/selfassessment', registrationData);
}

export async function authenticateWithGoogle(productType, payload) {
  return API.post(`/api/oauth/google${productType ? `?productType=${productType}` : ''} `, payload);
}

export async function fetchPrivacyPolicyData(accountId) {
  const response = await API.get(`/api/account/${accountId}/privacy_policy`);
  return response.data;
}

export async function updatePrivacyPolicyData(payload) {
  const response = await API.post('/api/account/privacy_policy', payload);
  return response.data;
}

export async function fetchAPIKey() {
  const response = await API.get('/api/management/credentials');
  return response.data;
}

export async function createAPIKey(description) {
  const response = await API.post('/api/management/credentials', { description });
  return response.data;
}

export async function revokeAPIKey(apiKeyId) {
  const response = await API.del(`/api/management/credentials/${apiKeyId}`);
  return response.data;
}

export async function fetchEmailTemplates(userId, productType) {
  const response = await API.get(`/api/users/${userId}/config/products/${productType}/templates/messages`);
  return response.data;
}

export async function createEmailTemplate(userId, productType, template) {
  const response = await API.post(`/api/users/${userId}/config/products/${productType}/templates/messages`, {
    ...template,
  });
  return response.data;
}

export async function updateEmailTemplate(userId, productType, templateId, template) {
  const response = await API.put(
    `/api/users/${userId}/config/products/${productType}/templates/messages/${templateId}`,
    { ...template }
  );
  return response.data;
}

export async function deleteEmailTemplate(userId, productType, templateId) {
  const response = await API.del(
    `/api/users/${userId}/config/products/${productType}/templates/messages/${templateId}`
  );
  return response.data;
}

export async function createWhitelabelConfig(accountId, payload) {
  const response = await API.post(`/api/account/${accountId}/whitelabel`, payload);
  return response.data;
}

export async function updateWhitelabelConfig(accountId, payload) {
  const response = await API.put(`/api/account/${accountId}/whitelabel`, payload);
  return response.data;
}

export async function deleteWhitelabelConfig(accountId) {
  const response = await API.del(`/api/account/${accountId}/whitelabel`);
  return response.data;
}

export async function fetchCustomPortfolios(accountId, paginationParams = {}) {
  let queryString = '';

  for (const [key, value] of Object.entries(paginationParams)) {
    if (value !== undefined && value !== null) {
      queryString += `&${key}=${value}`;
    }
  }

  queryString = queryString ? `?${queryString.slice(1)}` : '';
  const response = await API.get(`/api/account/${accountId}/investment-components${queryString}`);
  return response.data;
}

export async function fetchAllCustomPortfolios(accountId, options = {}) {
  const pageSize = 20;
  const portfolios = [];
  let page = 1;
  let maxPage = 1;

  do {
    // eslint-disable-next-line no-await-in-loop
    const response = await fetchCustomPortfolios(accountId, {
      ...options,
      page,
      pageSize,
    });

    const pagePortfolios = Array.isArray(response) ? response : response?.investmentComponents ?? [];
    portfolios.push(...pagePortfolios);
    maxPage = response?.maxPage ?? 1;
    page += 1;
  } while (page <= maxPage);

  return portfolios;
}

export async function createCustomPortfolio(accountId, portfolioData) {
  const response = await API.post(`/api/account/${accountId}/investment-components`, portfolioData);
  return response.data;
}

export async function fetchCustomPortfolio(accountId, portfolioId) {
  const response = await API.get(`/api/account/${accountId}/investment-components/${portfolioId}`);
  return response.data;
}

export async function updateCustomPortfolio(accountId, portfolioId, portfolioData) {
  const response = await API.put(`/api/account/${accountId}/investment-components/${portfolioId}`, portfolioData);
  return response.data;
}

export async function updateCustomPortfolioStatus(accountId, portfolioId, status) {
  const response = await API.put(`/api/account/${accountId}/investment-components/${portfolioId}/status`, { status });
  return response.data;
}

export async function deleteCustomPortfolio(accountId, portfolioId) {
  const response = await API.del(`/api/account/${accountId}/investment-components/${portfolioId}`);
  return response.data;
}

export async function fetchCustomPortfolioAllocation(accountId, userId) {
  const response = await API.get(`/api/account/${accountId}/user/${userId}/client-portfolio-allocation`);
  return response.data;
}

// the organization's currency (default for new investment components) and all supported currencies
export async function fetchAccountCurrencies(accountId) {
  const response = await API.get(`/api/account/${accountId}/currencies`);
  return response.data;
}

export async function saveCustomPortfolioAllocation(accountId, userId, allocations) {
  const response = await API.put(`/api/account/${accountId}/user/${userId}/client-portfolio-allocation`, {
    investmentComponentAllocations: allocations,
  });
  return response.data;
}

export async function calculateTemporaryCustomPortfolioAllocation(accountId, userId, allocations) {
  const response = await API.put(
    `/api/account/${accountId}/user/${userId}/client-portfolio-allocation/temporary?paramsType=LATEST`,
    {
      investmentComponentAllocations: allocations,
    }
  );
  return response.data;
}
